import type { FileExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import type { UploadQueue } from './UploadQueue'
import { createFileUploadData } from './FileUploadData'
import type { UploadGroupId } from './Upload'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'

export interface CordovaFile {
    data: Uint8Array
    dataURI: string
    mediaType: string
    name: string
    uri: string
}

export interface CordovaSelectSaveAndUpload {
    acceptedExtensions: FileExtension[]
    acceptedMimeTypes: string[]
    cordovaGetFile: (mimeFilter: string) => Promise<CordovaFile>
    fileId: FileId
    groupId?: UploadGroupId
    sizeLimitInBytes?: number
    writeFileContentToDisk: (fileId: FileId, data: CordovaFile) => Promise<void>
    uploadQueue: UploadQueue
    customer: number
    logger: ClientLogger
}

export async function cordovaSelectSaveAndUpload(args: CordovaSelectSaveAndUpload) {
    const {
        acceptedExtensions,
        acceptedMimeTypes,
        cordovaGetFile,
        fileId,
        groupId,
        sizeLimitInBytes,
        writeFileContentToDisk,
        uploadQueue,
        customer,
        logger,
    } = args
    // Ask the user to select a file and then read it into a buffer
    const file = await cordovaGetFile(acceptedMimeTypes.join(','))
    if (file == null) {
        logger.log("User didn't select a file")
        return null
    }

    // Create a the upload data for the file
    const fileUploadData = createFileUploadData(fileId, file.name, file.data.byteLength, file.mediaType)

    await uploadQueue.addUpload(fileUploadData, acceptedExtensions, sizeLimitInBytes, customer, groupId)

    try {
        logger.log('Starting writeFileContentToDisk.', { fileId })
        await writeFileContentToDisk(fileId, file)
        logger.log('Starting writeToDiskComplete.', { fileId })
        await uploadQueue.writeToDiskComplete(fileId, logger)
        logger.log('Updated queue that file was written to disk.', { fileId })
        return fileUploadData
    } catch (e) {
        logger.error(e, { fileId })
        await uploadQueue.failedToWriteToDisk(fileId, logger).catch((err) => logger.error(err, { fileId }))
        return null
    }
}
